import { faBars, faChevronDown, faPlus } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink, useLocation } from 'react-router-dom'
import { LogoutButtonMobile, LogoutMenuItem, UserMenuItemMobile, UserMenuLabel } from '~/features/auth'
import { BalanceMenuWidget, CashierButton } from '~/features/bank'
import assets from '../assets'
import { useAuth } from './Auth'
import { NavItem, accountItems, navGroups, visibleItems } from './navigation'
import { usePage } from './Page'

const linkBase = 'text-sm lg:text-base font-bold whitespace-nowrap uppercase tracking-wide'

/** One entry in the main bar — used when a group collapsed to a single visible item. */
const TopLevelLink = ({ item }: { item: NavItem }) =>
    item.external ? (
        <a href={item.to} target='blank' className={`${linkBase} py-2 hover:text-[#C91FFF]`}>
            {item.label}
        </a>
    ) : (
        <NavLink
            to={item.to}
            className={({ isActive }) =>
                `${linkBase} py-2 border-b-2 ${
                    isActive ? 'border-[#C91FFF]' : 'border-transparent hover:text-[#C91FFF]'
                }`
            }
        >
            {item.label}
        </NavLink>
    )

/** One row inside an open dropdown panel. */
const PanelLink = ({ item, onNavigate }: { item: NavItem; onNavigate: () => void }) => {
    const body = (
        <>
            <div className='text-sm font-bold uppercase tracking-wide'>{item.label}</div>
            {item.description && <div className='text-xs normal-case text-white/50 mt-0.5'>{item.description}</div>}
        </>
    )
    const className = 'block px-4 py-3 border-l-2 transition-colors'

    return item.external ? (
        <a
            href={item.to}
            target='blank'
            onClick={onNavigate}
            className={`${className} border-transparent hover:border-[#C91FFF] hover:bg-white/5`}
        >
            {body}
        </a>
    ) : (
        <NavLink
            to={item.to}
            onClick={onNavigate}
            className={({ isActive }) =>
                `${className} ${
                    isActive ? 'border-[#C91FFF] bg-white/5' : 'border-transparent hover:border-[#C91FFF] hover:bg-white/5'
                }`
            }
        >
            {body}
        </NavLink>
    )
}

/**
 * A group trigger split into two controls: the label navigates to the group's primary destination,
 * the chevron opens the panel.
 *
 * They are separate because making the whole trigger navigate would strand touch users. The desktop
 * bar starts at `md` (768px), which includes tablets, and there is no hover there — tap-to-open is
 * the only way those users reach the panel at all. Mouse users never need the chevron, since
 * hovering anywhere on the group still opens it.
 *
 * The primary destination is the first internal item, so the order in `navigation.ts` is what
 * decides where the parent click lands.
 *
 * The panel sits flush under the trigger with a padding bridge rather than a margin, so travelling
 * into it never crosses a dead gap that would close it.
 */
const NavDropdown = ({ label, items, isActive }: { label: string; items: NavItem[]; isActive: boolean }) => {
    const [open, setOpen] = useState(false)
    const wrapper = useRef<HTMLDivElement>(null)
    const location = useLocation()

    // Where clicking the group label goes. External links are skipped: an outbound URL is a poor
    // landing spot for someone who just wanted the section.
    const primary = items.find((item) => !item.external)

    useEffect(() => setOpen(false), [location.pathname])

    useEffect(() => {
        if (!open) return
        const onPointerDown = (e: PointerEvent) => {
            if (!wrapper.current?.contains(e.target as Node)) setOpen(false)
        }
        const onKeyDown = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
        document.addEventListener('pointerdown', onPointerDown)
        document.addEventListener('keydown', onKeyDown)
        return () => {
            document.removeEventListener('pointerdown', onPointerDown)
            document.removeEventListener('keydown', onKeyDown)
        }
    }, [open])

    return (
        <div
            ref={wrapper}
            className='relative'
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
        >
            <div
                className={`flex flex-row items-center border-b-2 ${
                    isActive ? 'border-[#C91FFF]' : 'border-transparent'
                } ${open || isActive ? '' : 'hover:text-[#C91FFF]'}`}
            >
                {primary ? (
                    <NavLink
                        to={primary.to}
                        onFocus={() => setOpen(true)}
                        className={`${linkBase} py-2`}
                        title={`${label}: go to ${primary.label}`}
                    >
                        {label}
                    </NavLink>
                ) : (
                    <button type='button' onClick={() => setOpen((prev) => !prev)} className={`${linkBase} py-2`}>
                        {label}
                    </button>
                )}
                <button
                    type='button'
                    aria-haspopup='true'
                    aria-expanded={open}
                    aria-label={`${label} menu`}
                    onClick={() => setOpen((prev) => !prev)}
                    onFocus={() => setOpen(true)}
                    className='flex items-center py-2 pl-2 pr-1'
                >
                    <FontAwesomeIcon
                        icon={faChevronDown}
                        className={`text-[0.6em] transition-transform ${open ? 'rotate-180' : ''}`}
                    />
                </button>
            </div>

            {open && (
                <div className='absolute top-full left-0 pt-3 z-50'>
                    <div className='min-w-[15rem] bg-black border border-white/20 shadow-2xl shadow-black py-1'>
                        {items.map((item) => (
                            <PanelLink key={item.to} item={item} onNavigate={() => setOpen(false)} />
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}

/** Avatar trigger with account-level entries. Staff reach Team Tools from here, not the main bar. */
const AccountMenu = ({ items }: { items: NavItem[] }) => {
    const [open, setOpen] = useState(false)
    const wrapper = useRef<HTMLDivElement>(null)
    const location = useLocation()

    useEffect(() => setOpen(false), [location.pathname])

    useEffect(() => {
        if (!open) return
        const onPointerDown = (e: PointerEvent) => {
            if (!wrapper.current?.contains(e.target as Node)) setOpen(false)
        }
        const onKeyDown = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
        document.addEventListener('pointerdown', onPointerDown)
        document.addEventListener('keydown', onKeyDown)
        return () => {
            document.removeEventListener('pointerdown', onPointerDown)
            document.removeEventListener('keydown', onKeyDown)
        }
    }, [open])

    /*
      The name and the caret are separate controls. Clicking your own name used to only toggle a
      panel that hover had already opened, so on a desktop it read as doing nothing; it now goes to
      /account, which is where that click was always aiming. The caret keeps the menu for everything
      else, and hover still opens it, so nothing became harder to reach.

      They are siblings rather than an anchor inside the button — nesting those is invalid markup,
      and the anchor would swallow the toggle. `group/usermenu` moves up to the wrapper so hovering
      either part still lights the level bar as one object.
    */
    return (
        <div
            ref={wrapper}
            className='relative flex flex-row items-center gap-2 py-1 group/usermenu'
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
        >
            <NavLink to='/account' onClick={() => setOpen(false)} className='flex flex-row items-center'>
                <UserMenuLabel />
            </NavLink>
            <button
                type='button'
                aria-haspopup='true'
                aria-expanded={open}
                aria-label='Account menu'
                onClick={() => setOpen((prev) => !prev)}
                className='flex items-center py-1 pr-1'
            >
                <FontAwesomeIcon
                    icon={faChevronDown}
                    className={`text-[0.6em] transition-transform ${
                        open ? 'rotate-180' : ''
                    } group-hover/usermenu:text-[#C91FFF]`}
                />
            </button>

            {open && (
                <div className='absolute top-full right-0 pt-3 z-50'>
                    <div className='min-w-[12rem] bg-black border border-white/20 shadow-2xl shadow-black py-1'>
                        {items.map((item) => (
                            <PanelLink key={item.to} item={item} onNavigate={() => setOpen(false)} />
                        ))}
                        <div className='mt-1 border-t border-white/10 pt-1'>
                            <LogoutMenuItem onLogout={() => setOpen(false)} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export function Menu() {
    const { auth } = useAuth()
    const [mobileMenuVisible, setMobileMenuVisible] = useState(false)
    const { header } = usePage()
    const { t } = useTranslation()
    const mobileMenu = useRef<HTMLDivElement>()
    const location = useLocation()

    // A group with nothing to show is dropped; a group down to one item becomes a plain link,
    // so the bar reshapes itself for logged-out visitors without a second set of definitions.
    const groups = navGroups
        .map((group) => ({ ...group, items: visibleItems(group.items, auth) }))
        .filter((group) => group.items.length > 0)

    // Dashboard is pulled out of the group list for the mobile drawer, where it is shown beside the
    // identity block instead. The desktop bar keeps using `groups` untouched.
    const dashboard = groups.find((g) => g.label === 'Dashboard')?.items[0]
    const navSections = groups.filter((g) => g.label !== 'Dashboard')

    const account = visibleItems(accountItems, auth)

    /*
      Signed out, the desktop bar is a single group — Play, holding Download and Guide — and a
      dropdown over two items is a menu whose only job is to hide a menu. Both are things a
      first-time visitor is here for, so they go straight on the bar as their own entries.

      Flattening every surviving group rather than special-casing Play keeps this a property of the
      signed-out bar instead of a rule about one label in `navigation.ts`. Signed in, nothing
      changes: there are four groups carrying nine entries, which is what the dropdowns are for.

      The mobile drawer is untouched — it already lists every item flat under its group heading.
    */
    const signedOut = !auth?.is_auth
    const flatItems = signedOut ? groups.flatMap((group) => group.items) : []

    const toggleMobileMenu = () => {
        setMobileMenuVisible((prev) => !prev)
    }

    useEffect(() => {
        if (mobileMenuVisible) {
            if (mobileMenu.current) mobileMenu.current.scrollTo(0, 0)
        }
    }, [mobileMenuVisible])

    const ref = useRef<HTMLDivElement>(null)
    useLayoutEffect(() => {
        if (!ref.current) return
        const ro = new ResizeObserver(() => {
            document.documentElement.style.setProperty(
                '--page-menu-height',
                `${ref.current.getBoundingClientRect().height}px`
            )
        })
        ro.observe(ref.current)
        return () => ro.disconnect()
    }, [])

    return (
        <div id='menu' className='fixed md:block h-16 md:h-auto top-0 md:relative z-50 mx-auto w-full'>
            <div ref={ref} className='relative  bg-black'>
                {/* `px-2`, matching left and right. The bar was padded 40px on the right against
                    8px on the left, so on a phone the skull sat on the edge while the two controls
                    opposite it floated a thumb-width short of theirs. */}
                <div className='relative flex flex-row items-center gap-x-4 lg:gap-x-6 py-2 md:py-4 px-2 lg:px-10 max-w-[1920px] mx-auto '>
                    <NavLink to='/' aria-label='BR1 home'>
                        <img src={assets.br1_skull_small} className='w-14 h-14 aspect-square object-contain' />
                    </NavLink>
                    {/*
                      Wrapping is the safety valve the original bar relied on. Between md and lg the
                      row carries four groups, three money figures and the account trigger; without
                      it a tight viewport overflows horizontally instead of growing a second line,
                      and the menu height is measured by a ResizeObserver that handles either.
                    */}
                    <div className='hidden md:flex flex-row flex-wrap items-center gap-x-5 gap-y-1 lg:gap-x-7'>
                        {signedOut
                            ? flatItems.map((item) => <TopLevelLink key={item.to} item={item} />)
                            : groups.map((group) =>
                                  group.items.length === 1 ? (
                                      <TopLevelLink key={group.label} item={group.items[0]} />
                                  ) : (
                                      <NavDropdown
                                          key={group.label}
                                          label={group.label}
                                          items={group.items}
                                          isActive={group.items.some(
                                              (item) => !item.external && location.pathname.startsWith(item.to)
                                          )}
                                      />
                                  )
                              )}
                    </div>
                    {/* `min-w-0` and `truncate`: this is a `flex-1` filler between the logo and, now,
                        two controls rather than one. Without them a long page title grows the row
                        instead of shortening itself. */}
                    <div className='md:hidden min-w-0 truncate uppercase font-bold text-center flex-1 md:flex-auto'>
                        {header}
                    </div>

                    <div className='hidden md:block flex-1'></div>
                    {auth?.is_auth && (
                        <div className='hidden md:flex flex-row items-center gap-6 lg:gap-8 shrink-0'>
                            <BalanceMenuWidget />
                            {/*
                              The balance and the way to change it, side by side, in one `shrink-0`
                              group so the flex row cannot squeeze the glyph the way it once crushed
                              the points icon to 0x0.

                              The gap is the wide one because the glyph reads as crowding the funds
                              figure at 16px. Widening it here is also what moves the money cluster
                              left: the bar's only flexible element is the `flex-1` spacer before it,
                              so every pixel added between these two comes out of that spacer rather
                              than off the right edge.
                            */}
                            <CashierButton />
                        </div>
                    )}

                    <div className='hidden md:block'>
                        {!auth?.is_auth === true ? (
                            /*
                              Sharp corners, and the hover is a brightness lift rather than the
                              browser's `outline`. `Button` (shared/ui) is `rounded-lg` with
                              `hover:outline`, which draws a squared-off ring around a rounded
                              button — the two shapes do not agree, and everything else on the site
                              has square corners.
                            */
                            <NavLink
                                to='/login'
                                className='hidden md:flex min-h-[2.5rem] items-center justify-center bg-[linear-gradient(270deg,#C91FFF_-15%,#03CEFF_170%)] px-5 text-sm font-black uppercase tracking-[0.15em] transition-[filter] hover:brightness-125'
                            >
                                {t('auth.signup')} / {t('auth.login')}
                            </NavLink>
                        ) : (
                            account.length > 0 && <AccountMenu items={account} />
                        )}
                    </div>

                    {/*
                      The cashier and the hamburger, as one cluster in the corner rather than two
                      items spaced off the row's 1rem gap. They are the bar's only two controls on
                      a phone and they belong to the same edge; at `gap-x-4` they read as two
                      separate things drifting in from the right, which is what the tight `gap-x-1`
                      and the shared wrapper fix.

                      The cashier being here at all is what removes a tap: the balances live inside
                      the drawer, so the only route to depositing used to be hamburger → a black
                      pill showing a number.
                    */}
                    <div className='md:hidden flex flex-row items-center gap-x-1 shrink-0'>
                        {auth?.is_auth && <CashierButton variant='icon' />}

                        <button
                            className='cursor-default min-h-[44px] min-w-[44px] flex items-center justify-center'
                            aria-label={mobileMenuVisible ? 'Close menu' : 'Open menu'}
                            aria-expanded={mobileMenuVisible}
                            onClick={toggleMobileMenu}
                        >
                            <FontAwesomeIcon
                                icon={mobileMenuVisible ? faPlus : faBars}
                                className={`text-3xl ${mobileMenuVisible ? 'rotate-45' : ''}`}
                            />
                        </button>
                    </div>
                </div>
            </div>

            {/*
              `100dvh` minus the bar, rather than `h-full`. A fixed element resolves percentage
              heights against the viewport, so `h-full` under `top-16` made the drawer 64px taller
              than the space it had — the last thing in it always sat below the fold. That did not
              matter while the content merely stacked from the top; it does now that the tail is
              pinned to the bottom, because the bottom was off-screen.
            */}
            <div
                ref={mobileMenu}
                className={`overscroll-contain md:hidden fixed top-16 overflow-x-hidden overflow-y-auto transition-all duration-500 w-full ${
                    mobileMenuVisible ? 'h-[calc(100dvh-4rem)]' : 'h-0'
                } bg-[linear-gradient(149.79deg,#C91FFF_13.94%,#03CEFF_91.37%)]`}
            >
                {/* `min-h-full` so the column always fills the drawer, which is what gives the
                    `mt-auto` at the end something to push against. Padding rather than margins:
                    margins would not be inside that height and would spill into a scrollbar.

                    The gaps are tight because the drawer lost 64px of (previously off-screen)
                    height: a staff account carries six links plus Team Tools, and at `gap-4` that
                    now overflowed by 29px, so the two things pinned to the bottom could only be
                    reached by scrolling — the opposite of the point. */}
                <div className='flex min-h-full flex-col gap-3 w-[86%] mx-auto pt-4 pb-[calc(1.5rem+env(safe-area-inset-bottom))]'>
                    {/*
                      Who you are and what you have, first. The nav groups used to come first and
                      pushed the level bar and the three balances to the very bottom, past the fold,
                      which is the wrong way round: those are the things you open the menu to check,
                      and the links are the things you already know are there.
                    */}
                    {auth?.is_auth === true && (
                        <div className='flex flex-col gap-2'>
                            <div onClick={() => setMobileMenuVisible(false)}>
                                <UserMenuItemMobile />
                            </div>
                            {/* The points figure in here is a link, and the drawer is a fixed
                                full-height panel — so without this the route changed behind it and
                                the tap looked like it had done nothing. Every other interactive
                                thing in this column already closes the drawer; this was the one
                                that did not. */}
                            <BalanceMenuWidget onNavigate={() => setMobileMenuVisible(false)} />
                            {/*
                              The CASHIER pill that used to sit here is gone. The bank glyph is in
                              the bar above and stays visible with the drawer open, so this was a
                              second, larger control for the same action a thumb's width below the
                              first — and the balances directly above it already open the cashier
                              when tapped. Three ways into one modal, stacked.
                            */}
                            {/*
                              Dashboard sits with the identity block rather than at the end of the
                              nav groups, and is filled rather than outlined.

                              It is the only entry here that is about *you* rather than about a part
                              of the game, so grouping it with your name and your balances is what it
                              actually belongs to. It was last in the list purely because
                              `navigation.ts` declares it last, which is a detail of that file rather
                              than a decision about priority.
                            */}
                            {dashboard && (
                                <NavLink
                                    onClick={() => setMobileMenuVisible(false)}
                                    to={dashboard.to}
                                    end
                                    className='mt-1 flex min-h-[44px] items-center justify-center rounded-3xl bg-black/80 text-base font-black uppercase tracking-[0.15em] shadow-lg'
                                >
                                    {dashboard.label}
                                </NavLink>
                            )}
                        </div>
                    )}

                    {navSections.map((group) => (
                        <div key={group.label} className='flex flex-col gap-2'>
                            {/* Headings only earn their space once a group holds more than one link. */}
                            {group.items.length > 1 && (
                                <div className='text-xs font-bold uppercase tracking-[0.25em] text-white/70 px-2'>
                                    {group.label}
                                </div>
                            )}
                            {group.items.map((item) =>
                                item.external ? (
                                    <a
                                        key={item.to}
                                        href={item.to}
                                        className='text-base font-bold text-center uppercase border rounded-3xl py-2 min-h-[44px] flex items-center justify-center'
                                    >
                                        {item.label}
                                    </a>
                                ) : (
                                    <NavLink
                                        key={item.to}
                                        onClick={() => setMobileMenuVisible(false)}
                                        to={item.to}
                                        end
                                        className={({ isActive }) =>
                                            `text-base font-bold text-center uppercase border rounded-3xl py-2 min-h-[44px] flex items-center justify-center ${
                                                isActive ? 'bg-[rgba(255,255,255,0.25)]' : ''
                                            }`
                                        }
                                    >
                                        {item.label}
                                    </NavLink>
                                )
                            )}
                        </div>
                    ))}

                    {/*
                      Account-level entries and Logout hug the bottom of the drawer, separated from
                      the nav by a rule.

                      They are not part of the browse list. Team Tools is staff-only — `access:
                      'staff'` in navigation.ts, so it never renders for a player — and Logout ends
                      the session; sitting them directly under MARKETPLACE reads as "one more place
                      to go", and puts the single destructive action a thumb-width from a link
                      people tap often. `mt-auto` consumes the spare height, and collapses to
                      nothing once the list is long enough to scroll.
                    */}
                    <div className='mt-auto flex flex-col gap-2 border-t border-white/25 pt-3'>
                        {!auth?.is_auth === true ? (
                            <NavLink
                                onClick={() => setMobileMenuVisible(false)}
                                to='/login'
                                className='text-base bg-black p-3 rounded-3xl border border-slate-500 font-bold text-center min-h-[44px]'
                            >
                                LOGIN
                            </NavLink>
                        ) : (
                            <>
                                {account
                                    .filter((item) => item.to !== '/account')
                                    .map((item) => (
                                        <NavLink
                                            key={item.to}
                                            onClick={() => setMobileMenuVisible(false)}
                                            to={item.to}
                                            className='text-base font-bold text-center uppercase border rounded-3xl py-2 min-h-[44px] flex items-center justify-center'
                                        >
                                            {item.label}
                                        </NavLink>
                                    ))}
                                <LogoutButtonMobile />
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}
